import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import TokenTable from "./TokenTable";
import { BRAND } from "../config/brand";

const data = [
  { name: "Treasury", value: 40, color: "#35d6ff" },
  { name: "Liquidity", value: 15, color: "#0ea5e9" },
  { name: "Partners", value: 15, color: "#4f8cff" },
  { name: "Team", value: 10, color: "#e7eef8" },
  { name: "Project Reserve", value: 10, color: "#d8b76a" },
  { name: "Advisors", value: 8, color: "#7dd3fc" },
  { name: "Airdrops", value: 2, color: "#8b5cf6" },
];

const pyramidLayers = [...data].reverse();

const getPyramidPoints = (index, total) => {
  const apexY = 96;
  const baseY = 328;
  const centerX = 180;
  const maxHalfWidth = 154;
  const step = (baseY - apexY) / total;
  const y1 = apexY + step * index;
  const y2 = apexY + step * (index + 1);
  const halfWidthAt = (y) => ((y - apexY) / (baseY - apexY)) * maxHalfWidth;
  const topHalf = halfWidthAt(y1);
  const bottomHalf = halfWidthAt(y2);

  return [
    `${centerX - topHalf},${y1}`,
    `${centerX + topHalf},${y1}`,
    `${centerX + bottomHalf},${y2}`,
    `${centerX - bottomHalf},${y2}`,
  ].join(" ");
};

const getLayerLabelY = (index, total) => {
  const apexY = 96;
  const baseY = 328;
  const step = (baseY - apexY) / total;
  return apexY + step * index + step / 2 + 5;
};

function TokenSection() {
  const tokenHeadRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeAllocation, setActiveAllocation] = useState("Treasury");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    const tokenHead = tokenHeadRef.current;

    if (tokenHead) {
      observer.observe(tokenHead);
    }

    return () => {
      if (tokenHead) {
        observer.unobserve(tokenHead);
      }
    };
  }, []);
  return (
    <div className="token-main">
      <motion.div
        className={`token-head ${isVisible ? "value" : ""}`}
        ref={tokenHeadRef}
        initial={{ opacity: 0, y: -50 }}
        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -50 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <span>TOKEN STATS</span>
        <h1>Tokenomics</h1>
        <p>
          The {BRAND.tokenSymbol} token is minted on BSC and is unique in that it can also be
          claimed, stored and traded on Binance Smart Chain.
        </p>
      </motion.div>

      <div className="token-body">
        <motion.div
          className="token-body-left"
          initial={{ opacity: 0, x: -100 }}
          animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <TokenTable text="Coin Name " num={BRAND.tokenName} />
          <TokenTable text="Symbol " num={BRAND.tokenSymbol} />
          <TokenTable text="Total Token to Allocate " num={BRAND.totalSupply} />
          <TokenTable text="Circulating Supply" num={BRAND.totalSupply} />
          <TokenTable text="Decimal " num={BRAND.tokenDecimals} />
          <TokenTable text="Chain " num={BRAND.chainName} />
          <TokenTable
            text="Token Address "
            num={BRAND.tokenAddress}
          />
        </motion.div>

        <motion.div
          className="token-body-right"
          initial={{ opacity: 0, x: 100 }}
          animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <div className="token-pyramid-card">
            <div className="token-pyramid-visual" aria-label="Token allocation pyramid">
              <svg
                className="token-pyramid-svg"
                viewBox="0 0 360 360"
                role="img"
                aria-label={`${BRAND.tokenSymbol} token allocation chart`}
              >
                <defs>
                  <filter id="tokenGlow" x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <linearGradient id="pyramidGrid" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="rgba(53, 214, 255, 0.18)" />
                    <stop offset="52%" stopColor="rgba(231, 238, 248, 0.05)" />
                    <stop offset="100%" stopColor="rgba(216, 183, 106, 0.16)" />
                  </linearGradient>
                </defs>

                <polygon
                  className="token-pyramid-frame"
                  points="180,18 18,334 342,334"
                />
                <line className="token-pyramid-center-line" x1="180" y1="18" x2="180" y2="334" />
                <polygon
                  className="token-pyramid-grid-fill"
                  points="180,18 18,334 342,334"
                />

                {pyramidLayers.map((item, index) => (
                  <g
                    className={`token-pyramid-slice ${activeAllocation === item.name ? "active" : ""
                      }`}
                    key={item.name}
                    style={{ "--slice-color": item.color }}
                    onMouseEnter={() => setActiveAllocation(item.name)}
                    onFocus={() => setActiveAllocation(item.name)}
                    tabIndex="0"
                  >
                    <polygon
                      points={getPyramidPoints(index, pyramidLayers.length)}
                    />
                    <text
                      x="180"
                      y={getLayerLabelY(index, pyramidLayers.length) - 3}
                      textAnchor="middle"
                    >
                      {item.value}%
                    </text>
                  </g>
                ))}

                <g className="token-pyramid-eye">
                  <ellipse cx="180" cy="76" rx="42" ry="17" />
                  <circle cx="180" cy="76" r="12" />
                  <circle cx="180" cy="76" r="4" />
                </g>
              </svg>
            </div>

            <div className="token-allocation-list">
              <div className="token-allocation-label">
                <span>Allocation Layers</span>
              </div>

              {data.map((item) => (
                <div
                  className={`token-allocation-row ${activeAllocation === item.name ? "active" : ""
                    }`}
                  key={item.name}
                  onMouseEnter={() => setActiveAllocation(item.name)}
                >
                  <i style={{ background: item.color }} />
                  <p>{item.name}</p>
                  <b
                    style={{
                      "--allocation-color": item.color,
                      "--allocation-width": `${Math.max(item.value, 8)}%`,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default TokenSection;
